<script lang="ts">
	import { ButtonV2, IconButton, Text, Icon, LiveIndicator } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import DashCameraWidgetHeader from 'src/components/Dashboards/DashWidgets/Components/DashDeviceWidgetCard/components/DashCameraWidgetHeader.svelte';
	import DashCameraOverlayActions from 'src/components/Dashboards/DashWidgets/Components/DashDeviceWidgetCard/components/DashCameraOverlayActions.svelte';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';

	// Size type following DS conventions
	type TileSize = 's' | 'm' | 'l';
	type TextStyleType = 'body-small' | 'tiny';

	// Props - pure presentation data
	export let name: string = '';
	export let isOnline: boolean = false;
	export let size: TileSize = 'm';
	export let embedMode = false;
	export let hideHeaderActions = false;
	export let hideBottomActions = false;
	export let fillContainer = false;
	/** Hide status/title overlay — used when parent renders camera chrome. */
	export let hideOverlay = false;
	export let showDashboardHeader = false;
	export let headerDevice: Device | undefined;
	export let headerStatus: DeviceStatusG2 | undefined;
	export let headerSettings: DeviceSettingsG2 | undefined;
	export let showLiveBadge = false;
	export let headerCompact = false;
	export let denseOverlay = false;
	export let useCloudConnectionIcon = false;
	export let showNameOnHover = false;
	export let showFullscreen = false;

	const dispatch = createEventDispatcher();

	// Size-based variants (following DS pattern)
	$: isLargeView = size === 'l';
	$: isSmallView = size === 's';

	// Button sizes based on tile size
	$: buttonSize = isLargeView ? ('m' as const) : ('s' as const);

	// Text styles based on size
	function getTitleTextStyle(isSmall: boolean): TextStyleType {
		return isSmall ? 'tiny' : 'body-small';
	}

	$: titleTextStyle = getTitleTextStyle(isSmallView);
</script>

<div
	class="camera-tile-widget"
	class:online={isOnline}
	class:offline={!isOnline}
	class:fill-container={fillContainer}
	class:dense-overlay={denseOverlay}
	class:show-name-on-hover={showNameOnHover}
>
	<!-- Media layer: VideoContainer handles all states (online, offline, privacy, error) -->
	<div class="camera-tile-media">
		<slot name="media" />
	</div>

	<!-- Overlay: header (status + title) + bottom actions -->
	{#if !hideOverlay}
		<div
			class="camera-tile-overlay d-flex flex-direction-column justify-content-between"
			class:large-view={isLargeView}
			class:has-dashboard-header={showDashboardHeader}
		>
			{#if showDashboardHeader && headerDevice}
				<div class="camera-tile-dashboard-header">
					<DashCameraWidgetHeader
						device={headerDevice}
						status={headerStatus}
						settings={headerSettings}
						{showLiveBadge}
						compact={headerCompact}
						{denseOverlay}
						{useCloudConnectionIcon}
					/>
				</div>
			{:else}
				<div
					class="camera-tile-overlay-top d-flex align-items-start justify-content-between gap-m"
				>
					{#if !embedMode}
						<div class="camera-tile-title-row d-flex align-items-center gap-xs">
							{#if isOnline}
								<LiveIndicator />
							{:else}
								<Icon
									icon="fal fa-circle"
									size="xs"
									class="text-color-muted ml-xs mr-xs"
								/>
							{/if}
							<Text styleType={titleTextStyle} class="text-bold text-white">
								{name}
							</Text>
						</div>
					{:else if headerDevice}
						<div class="camera-tile-embed-status">
							<DashCameraWidgetHeader
								device={headerDevice}
								status={headerStatus}
								settings={headerSettings}
								{showLiveBadge}
								compact={headerCompact}
							/>
						</div>
					{:else}
						<div></div>
					{/if}
					<slot name="headerAction">
						{#if hideHeaderActions}
							<button
								type="button"
								class="camera-tile-embed-menu-button"
								aria-label={$format('button_open_device_drawer', {
									default: 'Open device menu',
								})}
								on:click={() => dispatch('openDevice')}
							>
								<Icon
									icon="fa-regular fa-ellipsis-vertical"
									size={buttonSize === 'm' ? 's' : 'xs'}
									class="text-white"
								/>
							</button>
						{:else}
							<ButtonV2
								size={buttonSize}
								variant="primary"
								class="camera-tile-open-device-button"
								on:click={() => dispatch('openDevice')}
							>
								{$format('button_open_device', {
									default: 'Open device',
								})}
							</ButtonV2>
						{/if}
					</slot>
				</div>
			{/if}

			<div class="camera-tile-overlay-bottom">
				{#if showNameOnHover && name}
					<div class="camera-tile-hover-name">
						<Text styleType="body-extra-small" class="text-bold text-white" noWrap
							>{name}</Text
						>
					</div>
				{/if}
				{#if showDashboardHeader && headerDevice}
					<div class="camera-tile-corner-actions">
						<DashCameraOverlayActions
							showMenu
							showFullscreen={showFullscreen && isOnline && !hideBottomActions}
							dense={denseOverlay}
							on:menu-click={() => dispatch('openDevice')}
							on:fullscreen={() => dispatch('fullscreen')}
						/>
					</div>
				{:else if showFullscreen && isOnline && !hideBottomActions}
					<div class="camera-tile-bottom d-flex align-items-center justify-content-end">
						<slot name="bottomActions">
							<IconButton
								aria-label={$format('button_fullscreen', { default: 'Fullscreen' })}
								icon="fas fa-expand"
								variant="secondary"
								shape="circle"
								size={denseOverlay ? 's' : buttonSize}
								class={denseOverlay ? 'dense-fullscreen' : ''}
								on:click={() => dispatch('fullscreen')}
							/>
						</slot>
					</div>
				{/if}
			</div>
		</div>
	{/if}
</div>

<style>
	.camera-tile-widget {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		height: 100%;
		border-radius: var(--radius-extra-big);
		overflow: hidden;
		background-color: var(--color-1);
		--camera-overlay-inset: var(--spacing-s-plus);
	}

	.camera-tile-widget.dense-overlay {
		--camera-overlay-inset: var(--spacing-s);
	}

	.camera-tile-widget.fill-container {
		aspect-ratio: unset;
		min-height: 100%;
	}

	.camera-tile-widget.online {
		user-select: none;
		-webkit-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
	}

	.camera-tile-media {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
	}

	/* Overlay: consistent padding across all view sizes */
	.camera-tile-overlay {
		position: absolute;
		inset: 0;
		z-index: 2;
		padding: var(--spacing-s);
		pointer-events: none;
	}

	.camera-tile-overlay.large-view {
		padding: var(--spacing-m);
	}

	.camera-tile-overlay.has-dashboard-header {
		padding: 0;
	}

	.camera-tile-dashboard-header {
		display: flex;
		justify-content: flex-end;
		width: 100%;
		flex-shrink: 0;
		padding: var(--camera-overlay-inset);
		pointer-events: none;
	}

	.camera-tile-corner-actions {
		margin-left: auto;
		flex-shrink: 0;
		pointer-events: auto;
	}

	.camera-tile-embed-status {
		flex: 1;
		min-width: 0;
		pointer-events: none;
	}

	.camera-tile-overlay-top,
	.camera-tile-bottom {
		pointer-events: none;
	}

	.camera-tile-title-row {
		min-height: 20px;
	}

	.camera-tile-overlay::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.4) 0%,
			transparent 20%,
			transparent 70%,
			rgba(0, 0, 0, 0.4) 100%
		);
		opacity: 0;
		transition: opacity 0.2s ease;
		z-index: -1;
	}

	.camera-tile-widget:hover .camera-tile-overlay::before {
		opacity: 1;
	}

	.camera-tile-widget.offline .camera-tile-overlay::before {
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.2) 0%,
			transparent 20%,
			rgba(0, 0, 0, 0.6) 100%
		);
		opacity: 1;
	}
	.camera-tile-widget.offline .camera-tile-overlay {
		z-index: 2;
	}

	/* Open device button - hidden by default, shown on hover */
	.camera-tile-widget :global(.camera-tile-open-device-button) {
		opacity: 0;
		transition: opacity 0.2s ease-in-out;
		position: relative;
		z-index: 11;
		pointer-events: auto;
	}

	.camera-tile-widget:hover :global(.camera-tile-open-device-button) {
		opacity: 1;
	}

	.camera-tile-widget :global(.camera-tile-embed-menu-button) {
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0;
		padding: 6px 8px;
		border: none;
		background: none;
		cursor: pointer;
		flex-shrink: 0;
		pointer-events: auto;
		z-index: 11;
	}

	.camera-tile-widget :global(.camera-tile-embed-menu-button:focus-visible .icon-grid) {
		outline: 1px solid var(--color-primary);
		outline-offset: 4px;
		border-radius: 4px;
	}

	.camera-tile-overlay.has-dashboard-header :global(.camera-tile-open-device-button) {
		opacity: 1;
	}

	.camera-tile-bottom {
		z-index: 3;
		pointer-events: auto;
		margin-left: auto;
	}

	.camera-tile-bottom :global(*) {
		pointer-events: auto;
	}

	.camera-tile-overlay-bottom {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: var(--spacing-xs);
		width: 100%;
		padding: 0 var(--camera-overlay-inset) var(--camera-overlay-inset);
		box-sizing: border-box;
	}

	.camera-tile-hover-name {
		flex: 1;
		min-width: 0;
		margin-left: 15px;
		margin-bottom: 0;
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.2s ease;
	}

	.camera-tile-widget.show-name-on-hover:hover .camera-tile-hover-name,
	.camera-tile-widget.show-name-on-hover:focus-within .camera-tile-hover-name {
		opacity: 1;
	}

	.camera-tile-widget.dense-overlay :global(.dense-fullscreen.icon-button-v2),
	.camera-tile-widget.dense-overlay :global(button.dense-fullscreen) {
		width: 24px;
		height: 24px;
		min-width: 24px;
		min-height: 24px;
	}

	.camera-tile-widget.dense-overlay :global(.dense-fullscreen .icon-grid) {
		font-size: 12px;
	}
</style>
